import { useAuthStore } from '@/store/auth-store';

// =============================================================================
// API client â€” fetch wrapper dengan JWT auto-inject + error handling standar.
// Endpoint typed manual untuk sekarang; future generate dari OpenAPI spec
// (jalankan `npm run gen:api`).
// =============================================================================

const BASE = import.meta.env.VITE_API_BASE ?? '';

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = useAuthStore.getState().token;
  const res = await fetch(BASE + path, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init.headers,
    },
  });

  if (res.status === 401) {
    // Token expired / invalid â€” auto-logout supaya UI redirect ke /login.
    useAuthStore.getState().logout();
    throw new ApiError(401, 'Unauthorized');
  }
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new ApiError(res.status, text);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

// =============================================================================
// Type definitions â€” mirror struct Go di api-server/pkg/models/*.go.
// Sederhana dulu; full type bisa di-generate via openapi-typescript.
// =============================================================================

export interface LoginUser {
  id: number;
  real_name: string;
  login: string;
  token: string;
  phone: string;
  city: string;
  address: string;
  email: string;
  level: string;
  level_id: number;
  reseller: string;
  reseller_id: number;
  state: number;
  state_str: string;
}

export interface LoginSubordinate {
  id: number;
  real_name: string;
  login: string;
  level_id: number;
  reseller_id: number;
}

export interface LoginResponse {
  code: string;            // "SUCCESS" | "ERROR"
  msg: string;             // pesan human-readable
  user: LoginUser | null;  // null kalau error
  users: LoginSubordinate[] | null; // bawahan (admin/reseller)
}

export interface Vehicle {
  id: number;
  id_user: number;
  real_name: string;
  /** Telpon kontak pemilik (dari user.phone). Beda dengan `phone` di bawah
   *  yang merupakan nomor SIM card di GPS device. */
  owner_phone?: string;
  imei: string;
  nopol: string;
  phone: string;
  object_type: string;
  /** Nama model GPS device (vehicle.gps_model, mis. "FMB920") â†’ decode IO
   *  per-model di gateway. Dikirim juga di snapshot positions. */
  gps_model?: string;
  /** Field kaya berikut HANYA dikirim endpoint vehicleListAll (VehicleV2Row),
   *  bukan positions/monitoring â†’ optional. */
  gps_brand?: string;
  max_park: number;
  max_speed?: number;
  rent?: number;
  /** FK ke vehicle_brand / vehicle_model / gps_brand / gps_model.
   *  0 = unset. Dipakai untuk pre-select dropdown di edit drawer. */
  id_vhbrand?: number;
  id_vhmodel?: number;
  id_gpsbrand?: number;
  id_gpsmodel?: number;
  /** FK ke vehicle_group.id (0 = no group). */
  vh_group?: number;
  /** Resolved nama group via LEFT JOIN di backend. Empty kalau group dihapus. */
  vh_group_name?: string;
  /** Route integrasi posisi: none, bogasari, unilever, epool, stanley. */
  integration_route?: string;
  gps?: GpsPosition;
}

// =============================================================================
// Vehicle group
// =============================================================================

export interface VehicleGroup {
  id: number;
  id_user: number;
  vh_group: string;
}

export interface VehicleGroupListResponse {
  code: string;
  msg: string;
  total: number;
  rows: VehicleGroup[];
}

// Editable fields untuk EditVehicleDrawer â€” mirror VehicleV2.Update di backend
// (internal/handlers/vehicle_v2.go). Backend filter field sensitif by level_id:
//   - SEMUA: nopol/phone/object_type/gps_brand/max_*/fuel_type/active
//   - level_id 1+4: id_user
//   - level_id 1 only: install_date, guaranty_date
//
// Status/park/driver_id/vh_status/icon SENGAJA tidak di-include karena
// (a) frontend tidak edit, (b) MySQL error 1366 untuk integer column kalau
// kirim string kosong.
export interface VehicleEditPayload {
  /** Ganti IMEI device â€” hanya digubris backend untuk level_id 1 (admin) /
   *  4 (reseller); user lain diabaikan diam-diam (vehicle_service.Update). */
  imei?: string;
  nopol: string;
  phone: string;
  object_type: string;
  /** Nama model GPS device (vehicle.gps_model, mis. "FMB920") â†’ decode IO
   *  per-model di gateway. Free text. */
  gps_model?: string;
  gps_brand?: string;
  max_park: number;
  max_speed: number;
  max_idle: number;
  max_offline: number;
  active: number;
  fuel_type: string;
  // FK ke vehicle_brand.id / vehicle_model.id (0 = unset).
  id_vhbrand?: number;
  id_vhmodel?: number;
  // FK ke gps_brand.id / gps_model.id (0 = unset).
  id_gpsbrand?: number;
  id_gpsmodel?: number;
  // Reseller (4) + Admin (1) only
  id_user?: number;
  // Admin (1) only
  install_date?: string;
  guaranty_date?: string;
  integration_route?: string;
}

// =============================================================================
// Vehicle V2 (page Vehicles CRUD) â€” mirror VehicleV2 di backend.
// =============================================================================
export interface VehicleV2Row {
  id: number;
  id_user: number;
  imei: string;
  nopol: string;
  phone: string;
  object_type: string;
  gps_brand?: string;
  max_park: number;
  max_speed: number;
  max_idle: number;
  max_offline: number;
  active: number;
  park: number;
  fuel_type: string;
  vh_status: string;
  icon: string;
  status: string;
  install_date: string;
  guaranty_date: string;
  id_vhbrand: number;
  id_vhmodel: number;
  brand_name: string;  // derived
  model_name: string;  // derived
  id_gpsbrand: number;
  id_gpsmodel: number;
  gps_brand_name: string; // derived (JOIN gps_brand)
  gps_model_name: string; // derived (JOIN gps_model)
  integration_route?: string;
}

// =============================================================================
// Analog sensor calibration (vehicle_analog_sensors + calibration points).
// =============================================================================
export type AnalogChannel = 'adc1' | 'adc2';
export type AnalogConversionType = 'table' | 'linear';

export interface AnalogSensorRow {
  id: number;
  vehicle_id: number;
  channel: AnalogChannel;
  sensor_type: string;
  label: string;
  unit: string;
  conversion_type: AnalogConversionType;
  is_active: number;
  decimal_places: number;
  min_raw: number;
  max_raw: number;
  min_output: number;
  max_output: number;
  voltage_min: number;
  voltage_max: number;
}

export interface AnalogSensorForm {
  vehicle_id?: number;
  channel: AnalogChannel;
  sensor_type: string;
  label?: string;
  unit?: string;
  conversion_type?: AnalogConversionType;
  is_active?: number;
  decimal_places?: number;
  min_raw?: number;
  max_raw?: number;
  min_output?: number;
  max_output?: number;
  voltage_min?: number;
  voltage_max?: number;
}

export interface AnalogPointRow {
  id?: number;
  vehicle_id?: number;
  analog_sensor_id?: number;
  raw_value: number;
  mapped_value: number;
  notes?: string;
}

// =============================================================================
// Device Member â€” link vehicle â†” user untuk shared access.
// =============================================================================
export interface DeviceMemberRow {
  id: number;
  id_device: number;
  id_member: number;
  nopol: string;
  imei: string;
  real_name: string;
}

// =============================================================================
// Maintenance / Remainder â€” service reminder per vehicle.
// Backend: services.RemainderRow (view_remainder JOIN vehicle).
// due_date / due_odo = flag 0/1 dari worker (jatuh tempo).
// =============================================================================
export interface RemainderRow {
  id: number;
  vh_id: number;
  nopol: string;
  imei: string;
  real_name: string;
  service_name: string;
  service_date: string;   // "yyyy-MM-dd"
  service_odo: number;    // meter (target service)
  odo: number;            // meter (odo terakhir vehicle)
  due_date: number;       // 0/1 flag dari worker
  due_odo: number;        // 0/1 flag dari worker
}

export interface RemainderListResponse {
  total: number;
  rows: RemainderRow[];
}

// =============================================================================
// Rental â€” sewa-menyewa kendaraan. Scope by id_user (pengelola).
// Lihat database-schema/rental.sql untuk schema lengkap.
// =============================================================================

export type RentalStatus = 'active' | 'overdue' | 'returned' | 'cancelled';

export interface RentalRow {
  id: number;
  id_user: number;
  vh_id: number;
  nopol: string;
  imei: string;
  customer_name: string;
  customer_phone: string;
  customer_id_card: string;
  customer_address: string;
  start_date: string;      // "yyyy-MM-dd HH:mm:ss" WIB
  end_date: string;
  returned_at: string;     // "" kalau NULL
  rental_price: number;    // total tarif
  deposit: number;
  notes: string;
  status: RentalStatus;
  reminder_sent: number;
  create_at: string;
  update_at: string;
}

export interface RentalForm {
  id?: number;
  vh_id: number;
  customer_name: string;
  customer_phone: string;
  customer_id_card: string;
  customer_address: string;
  start_date: string;      // "yyyy-MM-dd HH:mm:ss"
  end_date: string;
  rental_price: number;
  deposit: number;
  notes: string;
}

export interface RentalListResponse {
  code: string;
  msg: string;
  total: number;
  rows: RentalRow[];
}

// =============================================================================
// Alarm history (tracks_alarm)
// =============================================================================

export interface AlarmHistoryRow {
  id: number;
  vh_id: number;
  nopol: string;
  imei: string;
  alarm_code: number;
  lat: number;
  lng: number;
  speed: number;
  address: string;
  timestamp: string; // "yyyy-MM-dd HH:mm:ss" WIB
  /** Nama geofence saat alarm (terisi untuk Masuk/Keluar Geofence). */
  gf?: string;
}

// =============================================================================
// Vehicle Cost â€” biaya sewa server per vehicle. Admin (level_id=1) only.
// =============================================================================

export interface VehicleCostRow {
  vh_id: number;
  nopol: string;
  imei: string;
  id_user: number;
  real_name: string;
  object_type: string;
  cost_server: number;  // Rp / bulan
  cost_id: number;      // 0 = belum di-set
}

export interface VehicleCostListResponse {
  code: string;
  msg: string;
  total: number;
  rows: VehicleCostRow[];
}

// =============================================================================
// Invoice â€” tagihan sewa server per user per periode.
// =============================================================================

export interface InvoiceRow {
  id: number;
  inv_year: number;
  inv_month: number;
  user_id: number;
  real_name: string;
  address: string;
  inv_date: string;     // "yyyy-MM-dd"
  inv_number: string;
  due_date: string;
  total: number;
  descr: string;
  paid: number;
  status: number;
  deleted: number;
  num_devices: number;
}

export interface InvoiceListResponse {
  code: string;
  msg: string;
  total: number;
  rows: InvoiceRow[];
}

export interface InvoiceGenerateRequest {
  year: number;
  month: number;        // 1-12
  user_ids?: number[];  // empty = semua user
  force?: boolean;      // overwrite existing
  due_in_days?: number; // default 14
  /** 'xlsx' = workbook Excel (1 sheet per customer, format download_invoice.php);
   *  default/'zip' = ZIP berisi HTML per customer. */
  output?: 'zip' | 'xlsx';
}


// =============================================================================
// Integration Unilever
// =============================================================================
export interface UnileverSetting {
  id_user: number;
  password?: string;
  vendor_id?: string;
  transporter_code?: string;
  transporter_name?: string;
  token?: string;
}

export interface UnileverShipmentRow {
  id: number;
  create_date?: string | null;
  vh_id: number;
  nopol?: string | null;
  imei?: string | null;
  id_user?: number | null;
  shipment_no?: string | null;
  provider_id?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  id_asal?: number | null;
  id_tujuan?: number | null;
  asal_name?: string | null;
  tujuan_name?: string | null;
  checkin_source?: string | null;
  checkin_dest?: string | null;
  checkout_source?: string | null;
  checkout_dest?: string | null;
  checkedin_source?: number | null;
  checkedout_source?: number | null;
  checkedin_dest?: number | null;
  checkedout_dest?: number | null;
  total_upload?: number | null;
  total_error?: number | null;
  last_upload?: string | null;
  last_error?: string | null;
  error_msg?: string | null;
  error_code?: string | null;
  msg?: string | null;
  closed?: number | null;
}

// Statistik upload Stanley per kendaraan (tabel stanley_shipments, read-only dari UI).
export interface StanleyShipmentRow {
  id: number;
  vh_id: number;
  nopol?: string | null;
  id_user?: number | null;
  shipment_no?: string | null;
  total_success?: number | null;
  total_error?: number | null;
  last_upload?: string | null;
  description?: string | null;
}

export interface UnileverShipmentForm {
  vh_id: number;
  shipment_no?: string;
  provider_id?: string;
  start_date?: string;
  end_date?: string;
  id_asal?: number;
  id_tujuan?: number;
  msg?: string;
}
// =============================================================================
// API Key
// =============================================================================
export interface ApiKey {
  id: number;
  user_id: number;
  user_name: string;
  /** 0 = per-user scope (semua kendaraan). >0 = per-vehicle scope (READ-ONLY). */
  vehicle_id: number;
  vehicle_nopol: string;
  name: string;
  key_value: string;
  blocked: number;
  expires_at: string;
  created_at: string;
  last_used_at: string;
  use_count: number;
}

export interface ApiKeyForm {
  user_id: number;
  /** 0 = per-user (akses semua kendaraan user). >0 = per-vehicle (read-only).
   *  Saat per-vehicle, user_id di-override backend ke owner vehicle. */
  vehicle_id?: number;
  name: string;
  expires_at: string; // "yyyy-MM-dd HH:mm:ss" or ""
}

// =============================================================================
// Master Checklist Kendaraan
// =============================================================================
export interface ChecklistMasterRow {
  id: number;
  /** Scope: 0 = system default (visible untuk semua user), >0 = owned by user. */
  id_user: number;
  category: string;     // "kondisi" | "kelengkapan"
  label: string;
  active: number;
  sort_order: number;
}

// =============================================================================
// Rental checklist + photo + report
// =============================================================================
export interface RentalChecklistRow {
  id: number;
  rental_id: number;
  master_id: number;
  category: string;
  label: string;
  state_start: 'ok' | 'damaged' | 'missing' | 'na';
  note_start: string;
  state_return: '' | 'ok' | 'damaged' | 'missing' | 'na';
  note_return: string;
}

export interface RentalPhotoRow {
  id: number;
  rental_id: number;
  phase: 'start' | 'return';
  file_path: string;     // URL public (MinIO atau /uploads/...)
  caption: string;
  uploaded_at: string;
}

export interface RentalReportRow {
  bucket: string;
  vh_id: number;
  nopol: string;
  brand_name: string;  // dari JOIN vehicle_brand (kosong kalau vehicle belum di-set brand)
  model_name: string;  // dari JOIN vehicle_model
  num_rentals: number;
  num_returned: number;
  num_overdue: number;
  num_active: number;
  num_cancelled: number;
  total_revenue: number;
  average_revenue: number;
}

// Share position â€” link publik untuk lihat posisi kendaraan tanpa login.
// CATATAN NAMA FIELD: api-server mengirim `start_date`/`finish_date` (bukan
// `*_at`), dan HANYA delapan field di bawah yang wajib ada. Sisanya opsional
// karena kolomnya tidak ada di semua deployment — tabel `share_positions` di
// pusatgps (rajagps) hanya lima kolom, tanpa id_user/view_count/revoked — dan
// karena itu tidak pernah ikut di respons. Tipe yang menjanjikannya sebagai
// wajib membuat UI menampilkan "Invalid Date" dan "undefined× dilihat".
export interface SharePositionRow {
  id: number;
  vh_id: number;
  token: string;
  start_date: string;  // saat dibuat
  finish_date: string; // '' = tanpa batas waktu
  nopol?: string;
  imei?: string;
  active: boolean;
  id_user?: number;
  label?: string;
  revoked?: number;
  view_count?: number;
}
export interface SharePublicData {
  nopol: string;
  imei: string;
  label: string;
  position: {
    lat: number; lng: number; speed: number; angle: number;
    batt_percent: number; charge: number;
    address: string; sdate: string;
  } | null;
}

// Tenant â€” match TenantRow di backend.
export interface TenantRow {
  id: number;
  name: string;
  phone: string;
  email: string;
  website: string;
  tax_id: string;
  contact_name: string;
  contact_phone: string;
  contact_email: string;
  address: string;
  city: string;
  province: string;
  country: string;
  postal_code: string;
  logo_url: string;
  timezone: string;
  locale: string;
  subscription_plan: string;
  max_devices: number;
  max_users: number;
  expired_at: string;
  notes: string;
  firebase_sa_path: string;
  active: number;
  created_at: string;
  updated_at: string;
  deleted_at?: string;
}
export type TenantForm = Omit<TenantRow, 'created_at' | 'updated_at' | 'deleted_at'>;

// WhatsApp session â€” shape match whatsapp-service NATS response.
export interface WhatsAppStatus {
  tenant_id: number;
  connected: boolean;
  phone_number?: string;
  jid?: string;
  client_exists?: boolean;
  awaiting_qr?: boolean;
}
export interface WhatsAppConnectResult {
  success: boolean;
  qr_code?: string;
  error?: string;
}
export interface WhatsAppQRResult {
  tenant_id: number;
  qr_code?: string;
}

// Notification preference â€” per user (1 row), tujuan WA + FCM untuk alarm.
// Single-tenant: tenant_id ada di env NOTIF_TENANT_ID di api-server, bukan
// per-user override.
export interface NotificationPref {
  id_user?: number;
  wa_enabled: number;
  wa_phone: string;
  fcm_enabled: number;
  fcm_user_id?: number | null;
  notify_power_cut: number;
  notify_low_batt: number;
  notify_enter_geo: number;
  notify_exit_geo: number;
  notify_overspeed: number;
  notify_sos: number;
  throttle_seconds: number;
  created_at?: string;
  updated_at?: string;
}

// Service record â€” riwayat servis/perbaikan kendaraan (history, bukan jadwal).
export interface ServicePartRow {
  id?: number;
  service_record_id?: number;
  part_name: string;
  part_code: string;
  quantity: number;
  unit: string;
  unit_price: number;
  total_price?: number;
  notes: string;
}
export interface ServiceRecordRow {
  id: number;
  id_user: number;
  vh_id: number;
  nopol: string;
  imei: string;
  service_date: string;
  service_type: string;
  service_name: string;
  description: string;
  location: string;
  address: string;
  assigned_to: string;
  assigned_phone: string;
  odo_meter: number;
  cost_parts: number;
  cost_labor: number;
  cost_other: number;
  cost_total: number;
  next_service_date: string;
  next_service_odo?: number;
  notes: string;
  created_at: string;
  updated_at: string;
  parts: ServicePartRow[];
}
export interface ServiceRecordForm {
  id?: number;
  vh_id: number;
  service_date: string;
  service_type: string;
  service_name: string;
  description: string;
  location: string;
  address: string;
  assigned_to: string;
  assigned_phone: string;
  odo_meter: number;
  cost_labor: number;
  cost_other: number;
  next_service_date?: string;
  next_service_odo?: number | null;
  notes: string;
  parts: ServicePartRow[];
}

// Command history â€” audit log perintah dikirim ke device + response.
export interface CommandHistoryRow {
  id: number;
  command_id: string;
  id_user: number;
  vh_id: number;
  nopol: string;
  imei: string;
  cmd_id: number;
  cmd_name: string;
  cmd_type: string;
  params: string;           // JSON string
  sent_at: string;
  status: 'sent' | 'responded' | 'failed' | 'timeout';
  stage: string;
  response_text: string;
  response_payload: string; // JSON string
  responded_at: string;
}

// Subset platform profile yang aman untuk endpoint public (login page).
export interface PlatformPublicProfile {
  company_name: string;
  contact_name: string;
  phone: string;
  email: string;
  address: string;
  website: string;
  logo_url: string;
}

// Platform profile (single row) â€” identitas platform untuk header invoice.
export interface PlatformProfile {
  company_name: string;
  contact_name: string;
  phone: string;
  email: string;
  address: string;
  website: string;
  tax_id: string;
  bank_info: string;
  logo_url: string;
  notes: string;
  updated_at?: string;
  updated_by?: number;
}

// Current user (self-service profile). Subset dari UserModel di backend.
export interface MyProfileData {
  id: number;
  login: string;
  real_name: string;
  phone: string;
  email: string;
  city: string;
  address: string;
  level_id: number;
  level: string;
  reseller_id: number;
  state: number;
}

// Vehicle brand & model (master data global, CRUD admin only â€” List terbuka).
export interface GpsBrandRow {
  id: number;
  gps_brand: string;
  sort_order: number;
  active: number;
}
export interface GpsBrandForm {
  id?: number;
  gps_brand: string;
  sort_order: number;
  active: number;
}
export interface GpsModelRow {
  id: number;
  id_gpsbrand: number;
  brand_name: string; // derived dari JOIN
  model_name: string;
  protocol: string;
  sort_order: number;
  active: number;
}
export interface GpsModelForm {
  id?: number;
  id_gpsbrand: number;
  model_name: string;
  protocol: string;
  sort_order: number;
  active: number;
}

export interface VehicleBrandRow {
  id: number;
  vh_brand: string;
  icon_static: string;
  icon_dynamic: string;
}
export interface VehicleBrandForm {
  id?: number;
  vh_brand: string;
  icon_static?: string;
  icon_dynamic?: string;
}
export interface VehicleModelRow {
  id: number;
  id_vhbrand: number;
  brand_name: string;  // derived (JOIN)
  model_name: string;
  sort_order: number;
  active: number;
}
export interface VehicleModelForm {
  id?: number;
  id_vhbrand: number;
  model_name: string;
  sort_order?: number;
  active?: number;
}

// Playback (history.tracks_report_YYYY_MM_DD)
export interface PlaybackPoint {
  lat: number;
  lng: number;
  speed: number;
  angle: number;
  time: string; // "yyyy-MM-dd HH:mm:ss" WIB
  address?: string;
  rpm?: number;
  external_voltage?: number;
  fuel_liter?: number;
  fuel_percent?: number;
  adc1?: number;
  adc2?: number;
  temp1?: number;
  temp2?: number;
  movement?: number;
  odo_device?: number;
}

export interface PlaybackRouteResponse {
  code: string;
  msg: string;
  total: number;
  rows: PlaybackPoint[];
}

export interface TripReportPoint {
  vh_id: number;
  sdate: string;
  tdate: string;
  tdate2: string;
  lat: number;
  lng: number;
  dist: number;
  liter: number;
  speed: number;
  angle: number;
  batt: number;
  acc: number;
  charge: number;
  fcut: number;
  address: string;
  poi: string;
  poi_id: number;
  gf: string;
  gf_id: number;
  alarm: number;
  alarm_info: string;
  fuel: number;
  price: number;
  park_info: string;
  temp: number;
  temp1: number;
  temp2: number;
  adc1: number;
  adc2: number;
  external_voltage: number;
  fuel_liter: number;
  fuel_percent: number;
  odo: number;
}

export interface TripReportSummary {
  distance: number;
  state: number;
  duration: string;
  start_date: string;
  stop_date: string;
  start_address: string;
  stop_address: string;
}

export interface TripReportResponse {
  code: string;
  msg: string;
  total: number;
  total_dist: number;
  total_dist_str: string;
  total_liter: number;
  total_price: number;
  total_park: string;
  total_move: string;
  total_idle: string;
  total_on: string;
  rows: TripReportPoint[];
  summaries: TripReportSummary[];
}

export interface GpsPosition {
  lat: number;
  lng: number;
  speed: number;
  angle: number;
  acc: number;
  fcut: number;
  charge: number;
  batt_percent: number;
  gsm: number;
  /** Sinyal GSM persen 0-100 â€” konversi per-protokol dilakukan DECODER backend
   *  (GT06 0-4, Teltonika 0-5, RSSI 0-31 â†’ persen). 0/undefined bila backend/DB
   *  lama belum punya kolom positions.gsm_percent (migration 0015). */
  gsm_percent?: number;
  sat: number;
  odo: number;          // meter (lihat docs/glossary.md)
  mileage: number;      // meter
  fuel: number;         // backend models.Position.Fuel = float64
  /** Field status device tambahan (Teltonika FMB dll.). Default 0 dari backend
   *  (COALESCE) bila device/protokol tak mengirim. */
  movement?: number;
  rpm?: number;
  adc1?: number;
  adc2?: number;
  temp1?: number;
  temp2?: number;
  external_voltage?: number; // Volt
  fuel_liter?: number;
  fuel_percent?: number;
  address: string;
  poi: string;
  gf: string;
  /** Label status (Moving/Park/Idle/Offline). Tidak dikirim backend di payload
   *  Position â€” diturunkan di client dari acc/speed/activity_state. Optional. */
  status?: string;
  /** State aktivitas dari processing: 1=Park 2=Idle 3=Move (models.State*). */
  activity_state?: number;
  /** Epoch ms mulainya aktivitas berjalan â€” durasi = now - activity_start. */
  activity_start?: number;
  /** Durasi state berjalan (DETIK) dari processing â€” sumber utama subtitle
   *  "Bergerak/Idle <durasi>"; fallback hitung client dari activity_start.
   *  park_info di bawah tetap ada untuk backward compatibility. */
  activity_state_duration?: number;
  park_info: string;
  alarm: number;
  rent: number;
  total_on: number;
  total_off: number;
  total_move: number;
  total_idle: number;
  total_park: number;
  total_odo: number;
  /** Statistik harian kanonik dari processing-service; total_* tetap ada untuk backward compatibility. */
  daily_move_seconds?: number;
  daily_idle_seconds?: number;
  daily_park_seconds?: number;
  daily_engine_seconds?: number;
  daily_distance?: number;
  /** Tidak selalu ada (banyak decoder kosong). Optional. */
  accon_info?: string;
  accoff_info?: string;
  tdate: string;        // "yyyy-MM-dd HH:mm:ss" WIB
  sdate: string;
  /** Flag dari gateway: 1 = device kirim packet tanpa GPS fix (positioned=0).
   *  Source: attributes.gpsNoData yang di-set di Gt06/WanWay decoder. Map
   *  marker + detail pane menampilkan badge "No GPS" saat ini = 1. */
  gpsNoData?: number;
  /** Flag kesehatan posisi dari processing-service (positions.valid):
   *  1 = sehat, 0 = GPS bermasalah (jam device dikoreksi / tdate macet).
   *  Fallback: attributes.clock_fixed / attributes.gps_stuck. */
  valid?: number;
  /** Full attributes bag dari position payload (kalau di-include). Frontend
   *  pakai untuk fallback baca gpsNoData kalau backend tidak flatten. */
  attributes?: Record<string, unknown>;
}

export interface PositionsResponse {
  code: string;
  msg: string;
  total: number;
  rows: Vehicle[];
}

// =============================================================================
// POI (customer POI dari maindb.poi)
// Backend Go: services.PoiRow (poi_service.go). lat/lng di-marshal sebagai string
// (json:"lat,string") tapi parser fetch standar otomatis convert ke number kalau
// kita assign ke field number. Kita biarkan string lalu parseFloat saat render.
// =============================================================================

export interface PoiRow {
  id: number;
  poi: string;        // nama POI
  descr: string;
  real_name: string;  // pemilik
  group: string;      // nama group (JOIN poi_groups) â€” '' kalau tidak di-assign
  contact: string;
  id_group: number;   // FK ke poi_groups.id (0 = no group)
  id_user: number;
  lat: string;        // string karena json:"lat,string" di backend
  lng: string;
}

export interface PoiListResponse {
  code: string;
  msg: string;
  total: number;
  rows: PoiRow[];
}

// =============================================================================
// Geofence
// Backend Go: services.GeofenceRow. `points` adalah WKT POLYGON string,
// mis. "POLYGON((106.8 -6.2, 106.81 -6.2, ...))". Helper parsePolygonWKT
// di src/lib/wkt.ts (kalau perlu render polygon di map).
// =============================================================================

export interface GeofenceRow {
  id: number;
  id_user: number;
  real_name: string;
  name: string;
  descr: string;
  vehicles: string;
  points: string;      // WKT POLYGON
  enter_alarm: number;
  exit_alarm: number;
  group_id: number;    // FK ke geofence_groups.id (0 = no group)
  group_name: string;  // derived dari JOIN
}

// Group entities â€” user-scoped (mirror VehicleGroup pattern).
export interface PoiGroupRow {
  id: number;
  user_id: number;
  name: string;
}
export interface GeofenceGroupRow {
  id: number;
  user_id: number;
  name: string;
}
export interface GroupForm {
  id?: number;
  user_id?: number;
  name: string;
}

export interface GeofenceListResponse {
  code: string;
  msg: string;
  rows: GeofenceRow[];
}

// =============================================================================
// Endpoint methods
// =============================================================================

export const api = {
  // --- Auth ---
  login: (login: string, password: string) =>
    request<LoginResponse>('/api/v1/login', {
      method: 'POST',
      body: JSON.stringify({ login, password }),
    }),

  // --- Vehicles & Positions ---
  positions: (userId: number) =>
    request<PositionsResponse>(`/api/v1/gps/positions/${userId}`, {
      method: 'POST',
    }),

  vehicles: () =>
    request<{ rows: Vehicle[] }>('/api/v1/vehicles'),

  vehicleUpdate: (body: Partial<VehicleEditPayload> & { id: number }) =>
    request<{ code: string; msg: string }>(`/api/v1/vehicles/${body.id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  // --- POI ---
  poiList: (userId: number) =>
    request<PoiListResponse>(`/api/v1/poi/list/${userId}`, { method: 'POST' }),

  // Backend PoiForm pakai json tag `lat,string` `lng,string` â†’ kirim sebagai STRING.
  poiCreate: (body: { id_user: number; poi: string; descr?: string; contact?: string; lat: string; lng: string; id_group?: number }) =>
    request<{ code: string; msg: string }>('/api/v1/poi/add', {
      method: 'POST', body: JSON.stringify(body),
    }),
  poiEdit: (body: { id: number; id_user: number; poi: string; descr?: string; contact?: string; lat: string; lng: string; id_group?: number }) =>
    request<{ code: string; msg: string }>('/api/v1/poi/edit', {
      method: 'POST', body: JSON.stringify(body),
    }),
  poiDelete: (id: number) =>
    request<{ code: string; msg: string }>('/api/v1/poi/delete', {
      method: 'POST', body: JSON.stringify({ id }),
    }),

  // POI groups â€” user-scoped CRUD.
  poiGroupList: (idUser?: number) => {
    const q = idUser && idUser > 0 ? `?id_user=${idUser}` : '';
    return request<{ code: string; msg: string; total: number; rows: PoiGroupRow[] }>(
      `/api/v1/poi-group/list${q}`, { method: 'GET' });
  },
  poiGroupAdd: (body: GroupForm) =>
    request<{ code: string; msg: string }>('/api/v1/poi-group/add', {
      method: 'POST', body: JSON.stringify(body),
    }),
  poiGroupUpdate: (body: GroupForm & { id: number }) =>
    request<{ code: string; msg: string }>('/api/v1/poi-group/update', {
      method: 'POST', body: JSON.stringify(body),
    }),
  poiGroupDelete: (id: number) =>
    request<{ code: string; msg: string }>('/api/v1/poi-group/delete', {
      method: 'POST', body: JSON.stringify({ id }),
    }),

  // --- Geofence ---
  geofenceList: (userId: number) =>
    request<GeofenceListResponse>(`/api/v1/geofence/list/${userId}`, { method: 'POST' }),

  geofenceCreate: (body: { id_user: number; name: string; descr?: string; points: { lat: number; lng: number }[]; enter_alarm: number; exit_alarm: number; group_id?: number; vehicles?: string }) =>
    request<{ code: string; msg: string }>('/api/v1/geofence/add', {
      method: 'POST', body: JSON.stringify(body),
    }),
  geofenceEdit: (body: { id: number; id_user: number; name: string; descr?: string; points: { lat: number; lng: number }[]; enter_alarm: number; exit_alarm: number; group_id?: number; vehicles?: string }) =>
    request<{ code: string; msg: string }>('/api/v1/geofence/edit', {
      method: 'POST', body: JSON.stringify(body),
    }),
  geofenceDelete: (id: number) =>
    request<{ code: string; msg: string }>('/api/v1/geofence/delete', {
      method: 'POST', body: JSON.stringify({ id }),
    }),

  // Geofence groups â€” user-scoped CRUD.
  geofenceGroupList: (idUser?: number) => {
    const q = idUser && idUser > 0 ? `?id_user=${idUser}` : '';
    return request<{ code: string; msg: string; total: number; rows: GeofenceGroupRow[] }>(
      `/api/v1/geofence-group/list${q}`, { method: 'GET' });
  },
  geofenceGroupAdd: (body: GroupForm) =>
    request<{ code: string; msg: string }>('/api/v1/geofence-group/add', {
      method: 'POST', body: JSON.stringify(body),
    }),
  geofenceGroupUpdate: (body: GroupForm & { id: number }) =>
    request<{ code: string; msg: string }>('/api/v1/geofence-group/update', {
      method: 'POST', body: JSON.stringify(body),
    }),
  geofenceGroupDelete: (id: number) =>
    request<{ code: string; msg: string }>('/api/v1/geofence-group/delete', {
      method: 'POST', body: JSON.stringify({ id }),
    }),

  // --- Remainder / Maintenance ---
  remainderList: () =>
    request<RemainderListResponse>('/api/v1/remainder/list', { method: 'POST' }),

  remainderListByUser: (idUser: number) =>
    request<RemainderListResponse>('/api/v1/remainder/listbyuser', {
      method: 'POST', body: JSON.stringify({ id: idUser }),
    }),

  remainderAdd: (body: { vh_id: number; service_name: string; service_date: string; service_odo: number }) =>
    request<{ code: string; msg: string }>('/api/v1/remainder/add', {
      method: 'POST', body: JSON.stringify(body),
    }),

  remainderEdit: (body: { id: number; vh_id: number; service_name: string; service_date: string; service_odo: number }) =>
    request<{ code: string; msg: string }>('/api/v1/remainder/edit', {
      method: 'POST', body: JSON.stringify(body),
    }),

  remainderDelete: (id: number) =>
    request<{ code: string; msg: string }>('/api/v1/remainder/delete', {
      method: 'POST', body: JSON.stringify({ id }),
    }),

  // --- User CRUD ---
  // Backend (user_service.go Add/Edit) auto-set reseller_id=caller.id kalau
  // caller level_id=4. Admin (1) bebas pilih level_id (2 atau 4). Form di FE
  // tinggal kirim payload yang relevan.
  userAdd: (body: {
    login: string; password: string; real_name?: string; phone?: string;
    email?: string; address?: string; level_id: number;
  }) =>
    request<{ code: string; msg: string }>('/api/v1/user/add', {
      method: 'POST', body: JSON.stringify(body),
    }),

  userEdit: ({ id, ...body }: {
    id: number; real_name?: string; phone?: string; email?: string;
    address?: string; level_id?: number; password?: string;
  }) =>
    request<{ code: string; msg: string }>(`/api/v1/users/${id}`, {
      method: 'PATCH', body: JSON.stringify(body),
    }),

  userDelete: (id: number) =>
    request<{ code: string; msg: string }>('/api/v1/user/delete', {
      method: 'POST', body: JSON.stringify({ id }),
    }),

  userEnable: (id: number) =>
    request<{ code: string; msg: string }>('/api/v1/user/enable', {
      method: 'POST', body: JSON.stringify({ id }),
    }),

  userDisable: (id: number) =>
    request<{ code: string; msg: string }>('/api/v1/user/disable', {
      method: 'POST', body: JSON.stringify({ id }),
    }),

  // --- Vehicle CRUD ---
  // List dipakai oleh page Vehicles. Untuk monitoring tetap pakai positions().
  // idUser optional â€” kalau diisi (>0), backend filter WHERE v.id_user = ?
  // (dipakai admin/reseller untuk lihat vehicle milik user tertentu). Tanpa
  // idUser, backend return semua vehicle sesuai scope JWT user.
  vehicleListAll: (idUser?: number) => {
    const q = idUser && idUser > 0 ? `?id_user=${idUser}` : '';
    return request<{ code: string; msg: string; total: number; rows: VehicleV2Row[] }>(
      `/api/v1/vehicles${q}`, { method: 'GET' });
  },

  vehicleCreate: (body: Partial<VehicleEditPayload> & { imei: string; nopol: string; id_user?: number }) =>
    request<{ code: string; msg: string; id: number }>('/api/v1/vehicles', {
      method: 'POST', body: JSON.stringify(body),
    }),

  vehicleDelete: (id: number) =>
    request<{ code: string; msg: string }>(`/api/v1/vehicles/${id}`, {
      method: 'DELETE',
    }),

  // --- Analog calibration ---
  analogSensorList: (vehicleId: number) =>
    request<{ total: number; rows: AnalogSensorRow[] }>(
      `/api/v1/vehicle/${vehicleId}/analog-sensors`, { method: 'GET' }),

  analogSensorCreate: (vehicleId: number, body: AnalogSensorForm) =>
    request<{ code: string; msg: string; id: number }>(
      `/api/v1/vehicle/${vehicleId}/analog-sensors`, {
        method: 'POST',
        body: JSON.stringify({ ...body, vehicle_id: vehicleId }),
      }),

  analogSensorUpdate: (sensorId: number, body: AnalogSensorForm) =>
    request<{ code: string; msg: string; id: number }>(
      `/api/v1/analog-sensors/${sensorId}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),

  analogSensorDelete: (sensorId: number) =>
    request<{ code: string; msg: string }>(
      `/api/v1/analog-sensors/${sensorId}`, { method: 'DELETE' }),

  analogPointList: (sensorId: number) =>
    request<{ total: number; rows: AnalogPointRow[] }>(
      `/api/v1/analog-sensors/${sensorId}/points`, { method: 'GET' }),

  analogPointReplace: (sensorId: number, points: AnalogPointRow[]) =>
    request<{ code: string; msg: string; count: number }>(
      `/api/v1/analog-sensors/${sensorId}/points`, {
        method: 'PUT',
        body: JSON.stringify({ points }),
      }),

  // --- Device Member (mengikuti backend: /api/v1/members) ---
  // List: GET /api/v1/members?id_member=  (device milik user) ATAU ?id_device= (anggota device).
  dmListByUser: (idMember: number) =>
    request<{ code: string; msg: string; total: number; rows: DeviceMemberRow[] }>(
      `/api/v1/members?id_member=${idMember}`, { method: 'GET' }),

  dmListByDevice: (idDevice: number) =>
    request<{ code: string; msg: string; total: number; rows: DeviceMemberRow[] }>(
      `/api/v1/members?id_device=${idDevice}`, { method: 'GET' }),

  dmAdd: (body: { id_device: number; id_member: number }) =>
    request<{ code: string; msg: string; id: number }>('/api/v1/members', {
      method: 'POST', body: JSON.stringify(body),
    }),

  dmDelete: (id: number) =>
    request<{ code: string; msg: string }>(`/api/v1/members?id=${id}`, {
      method: 'DELETE',
    }),

  // --- Rental ---
  // Backend scope by JWT user_id + level_id otomatis (admin lihat semua,
  // selainnya hanya milik sendiri).
  rentalList: () =>
    request<RentalListResponse>('/api/v1/rental/list', { method: 'POST' }),

  rentalAdd: (body: RentalForm) =>
    request<{ code: string; msg: string }>('/api/v1/rental/add', {
      method: 'POST', body: JSON.stringify(body),
    }),

  rentalEdit: (body: RentalForm & { id: number }) =>
    request<{ code: string; msg: string }>('/api/v1/rental/edit', {
      method: 'POST', body: JSON.stringify(body),
    }),

  rentalDelete: (id: number) =>
    request<{ code: string; msg: string }>('/api/v1/rental/delete', {
      method: 'POST', body: JSON.stringify({ id }),
    }),

  rentalReturn: (id: number) =>
    request<{ code: string; msg: string }>('/api/v1/rental/return', {
      method: 'POST', body: JSON.stringify({ id }),
    }),

  rentalCancel: (id: number) =>
    request<{ code: string; msg: string }>('/api/v1/rental/cancel', {
      method: 'POST', body: JSON.stringify({ id }),
    }),

  // --- Vehicle Cost (admin only) ---
  vehicleCostList: (idUser?: number) =>
    request<VehicleCostListResponse>(
      `/api/v1/vehicle-cost/list${idUser ? `?id_user=${idUser}` : ''}`,
      { method: 'GET' },
    ),

  vehicleCostSet: (body: { vh_id: number; cost_server: number }) =>
    request<{ code: string; msg: string }>('/api/v1/vehicle-cost/set', {
      method: 'POST', body: JSON.stringify(body),
    }),

  vehicleCostSetBulk: (body: { vh_ids: number[]; cost_server: number }) =>
    request<{ code: string; msg: string; succeeded: number }>('/api/v1/vehicle-cost/setbulk', {
      method: 'POST', body: JSON.stringify(body),
    }),

  vehicleCostDelete: (vhId: number) =>
    request<{ code: string; msg: string }>('/api/v1/vehicle-cost/delete', {
      method: 'POST', body: JSON.stringify({ vh_id: vhId }),
    }),

  vehicleCostDeleteBulk: (vhIds: number[]) =>
    request<{ code: string; msg: string; succeeded: number }>('/api/v1/vehicle-cost/deletebulk', {
      method: 'POST', body: JSON.stringify({ vh_ids: vhIds }),
    }),

  // --- Invoice (admin only) ---
  invoiceList: (q: { year?: number; month?: number; user_id?: number } = {}) => {
    const params = new URLSearchParams();
    if (q.year)    params.set('year',    String(q.year));
    if (q.month)   params.set('month',   String(q.month));
    if (q.user_id) params.set('user_id', String(q.user_id));
    const qs = params.toString();
    return request<InvoiceListResponse>(`/api/v1/invoice/list${qs ? '?' + qs : ''}`, { method: 'GET' });
  },

  invoiceDelete: (id: number) =>
    request<{ code: string; msg: string }>('/api/v1/invoice/delete', {
      method: 'POST', body: JSON.stringify({ id }),
    }),

  /**
   * invoiceGenerate â€” return raw Response supaya caller bisa stream blob ZIP
   * langsung ke browser download. Bypass json parser di `request<T>`.
   */
  invoiceGenerate: async (body: InvoiceGenerateRequest): Promise<Response> => {
    const token = (await import('@/store/auth-store')).useAuthStore.getState().token;
    const res = await fetch((import.meta.env.VITE_API_BASE ?? '') + '/api/v1/invoice/generate', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
    });
    return res;
  },

  /**
   * invoiceDownload â€” download invoice yang SUDAH di-generate untuk satu
   * periode sebagai workbook Excel (XLSX, 1 sheet per customer â€” format
   * download_invoice.php). Return raw Response untuk di-stream sebagai blob.
   */
  invoiceDownload: async (year: number, month: number, userId?: number): Promise<Response> => {
    const token = (await import('@/store/auth-store')).useAuthStore.getState().token;
    const p = new URLSearchParams({ year: String(year), month: String(month) });
    if (userId) p.set('user_id', String(userId));
    return fetch((import.meta.env.VITE_API_BASE ?? '') + `/api/v1/invoice/download?${p.toString()}`, {
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
  },

  // --- Alarm history (EventsPanel) ---
  // limit + offset = pagination. id_user = impersonation target (admin/reseller).
  alarmRecent: (params: { limit?: number; offset?: number; id_user?: number; vh_id?: number; from?: string; to?: string } = {}) => {
    const p = new URLSearchParams();
    p.set('limit',  String(params.limit  ?? 50));
    p.set('offset', String(params.offset ?? 0));
    if (params.id_user) p.set('id_user', String(params.id_user));
    if (params.vh_id) p.set('vh_id', String(params.vh_id));
    if (params.from) p.set('from', params.from);
    if (params.to) p.set('to', params.to);
    return request<{ code: string; msg: string; total: number; rows: AlarmHistoryRow[] }>(
      `/api/v1/alarm/recent?${p.toString()}`, { method: 'GET' });
  },
  alarmDelete: (id: number) =>
    request<{ code: string; msg: string }>('/api/v1/alarm/delete', {
      method: 'POST', body: JSON.stringify({ id }),
    }),
  alarmDeleteBulk: (ids: number[]) =>
    request<{ code: string; msg: string; succeeded: number }>('/api/v1/alarm/delete-bulk', {
      method: 'POST', body: JSON.stringify({ ids }),
    }),

  // --- Integration: Unilever ---
  unileverSettingGet: (idUser: number) =>
    request<{ code: string; found: boolean; data?: UnileverSetting }>(
      `/api/v1/integration/unilever-setting/${idUser}`, { method: 'GET' }),
  unileverSettingSave: (body: UnileverSetting) =>
    request<{ code: string; msg?: string }>('/api/v1/integration/unilever-setting', {
      method: 'POST', body: JSON.stringify(body),
    }),
  unileverSettingGenerateToken: (body: { id_user: number; vendor_id?: string; password?: string }) =>
    request<{ code: string; token: string; msg?: string }>('/api/v1/integration/unilever-setting/generate-token', {
      method: 'POST', body: JSON.stringify(body),
    }),
  unileverShipmentList: (params?: { id_user?: number; include_closed?: boolean }) => {
    const p = new URLSearchParams();
    if (params?.id_user) p.set('id_user', String(params.id_user));
    if (params?.include_closed) p.set('include_closed', '1');
    const qs = p.toString();
    return request<{ code: string; rows: UnileverShipmentRow[] }>(
      `/api/v1/integration/unilever/shipments${qs ? `?${qs}` : ''}`, { method: 'GET' });
  },
  stanleyShipmentList: (idUser?: number) => {
    const qs = idUser ? `?id_user=${idUser}` : '';
    return request<{ code: string; total: number; rows: StanleyShipmentRow[] }>(
      `/api/v1/integration/stanley/shipments${qs}`, { method: 'GET' });
  },
  unileverShipmentCreate: (body: UnileverShipmentForm) =>
    request<{ code: string; data: UnileverShipmentRow }>('/api/v1/integration/unilever/shipments', {
      method: 'POST', body: JSON.stringify(body),
    }),
  unileverShipmentUpdate: (id: number, body: UnileverShipmentForm) =>
    request<{ code: string; data: UnileverShipmentRow }>(`/api/v1/integration/unilever/shipments/${id}`, {
      method: 'PUT', body: JSON.stringify(body),
    }),
  unileverShipmentClose: (id: number, body?: { end_date?: string }) =>
    request<{ code: string; data: UnileverShipmentRow }>(`/api/v1/integration/unilever/shipments/${id}/close`, {
      method: 'POST', body: JSON.stringify(body ?? {}),
    }),
  unileverShipmentDelete: (id: number) =>
    request<{ code: string; msg?: string }>(`/api/v1/integration/unilever/shipments/${id}`, {
      method: 'DELETE',
    }),

  // --- API Key (admin only) ---
  apiKeyList: () =>
    request<{ code: string; msg: string; total: number; rows: ApiKey[] }>(
      '/api/v1/api-key/list', { method: 'GET' }),
  apiKeyGenerate: (body: ApiKeyForm) =>
    request<{ code: string; msg: string; data: ApiKey }>('/api/v1/api-key/generate', {
      method: 'POST', body: JSON.stringify(body),
    }),
  apiKeyUpdate: (id: number, body: ApiKeyForm) =>
    request<{ code: string; msg: string }>(`/api/v1/api-key/${id}`, {
      method: 'POST', body: JSON.stringify(body),
    }),
  apiKeySetBlocked: (id: number, blocked: boolean) =>
    request<{ code: string; msg: string }>('/api/v1/api-key/block', {
      method: 'POST', body: JSON.stringify({ id, blocked }),
    }),
  apiKeyDelete: (id: number) =>
    request<{ code: string; msg: string }>('/api/v1/api-key/delete', {
      method: 'POST', body: JSON.stringify({ id }),
    }),

  // --- Master Checklist ---
  checklistMasterList: (params: { only_active?: boolean; category?: string } = {}) => {
    const p = new URLSearchParams();
    if (params.only_active) p.set('only_active', '1');
    if (params.category)    p.set('category', params.category);
    const qs = p.toString();
    return request<{ code: string; msg: string; total: number; rows: ChecklistMasterRow[] }>(
      `/api/v1/checklist-master/list${qs ? '?' + qs : ''}`, { method: 'GET' });
  },
  checklistMasterAdd: (body: { category: string; label: string; active: number; sort_order: number }) =>
    request<{ code: string; msg: string }>('/api/v1/checklist-master/add', {
      method: 'POST', body: JSON.stringify(body),
    }),
  checklistMasterEdit: (body: { id: number; category: string; label: string; active: number; sort_order: number }) =>
    request<{ code: string; msg: string }>('/api/v1/checklist-master/edit', {
      method: 'POST', body: JSON.stringify(body),
    }),
  checklistMasterDelete: (id: number) =>
    request<{ code: string; msg: string }>('/api/v1/checklist-master/delete', {
      method: 'POST', body: JSON.stringify({ id }),
    }),

  // --- Rental Checklist + Photo + Report ---
  rentalChecklistList: (rentalId: number) =>
    request<{ code: string; msg: string; total: number; rows: RentalChecklistRow[] }>(
      `/api/v1/rental/checklist/${rentalId}`, { method: 'GET' }),
  rentalChecklistBulkUpdate: (body: { phase: 'start' | 'return'; items: { id: number; state: string; note: string }[] }) =>
    request<{ code: string; msg: string }>('/api/v1/rental/checklist/bulk', {
      method: 'POST', body: JSON.stringify(body),
    }),
  // Manual re-seed rental_checklist dari vehicle_checklist_master. Dipakai untuk
  // rental lama yang belum ada checklist (sebelum auto-seed di Add aktif).
  rentalChecklistSeed: (rentalId: number) =>
    request<{ code: string; msg: string }>('/api/v1/rental/checklist/seed', {
      method: 'POST', body: JSON.stringify({ rental_id: rentalId }),
    }),
  rentalPhotoList: (rentalId: number, phase?: string) =>
    request<{ code: string; msg: string; total: number; rows: RentalPhotoRow[] }>(
      `/api/v1/rental/photo/${rentalId}${phase ? '?phase=' + phase : ''}`, { method: 'GET' }),
  rentalPhotoUpload: async (rentalId: number, phase: 'start' | 'return', file: File, caption?: string) => {
    const token = (await import('@/store/auth-store')).useAuthStore.getState().token;
    const fd = new FormData();
    fd.append('rental_id', String(rentalId));
    fd.append('phase', phase);
    if (caption) fd.append('caption', caption);
    fd.append('file', file);
    const res = await fetch((import.meta.env.VITE_API_BASE ?? '') + '/api/v1/rental/photo/upload', {
      method: 'POST',
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: fd,
    });
    return res.json() as Promise<{ code: string; msg: string; id?: number; file_path?: string }>;
  },
  rentalPhotoDelete: (id: number) =>
    request<{ code: string; msg: string }>('/api/v1/rental/photo/delete', {
      method: 'POST', body: JSON.stringify({ id }),
    }),
  rentalReport: (params: { from?: string; to?: string; group?: 'vehicle' | 'total'; id_user?: number } = {}) => {
    const p = new URLSearchParams();
    if (params.from)    p.set('from', params.from);
    if (params.to)      p.set('to', params.to);
    if (params.group)   p.set('group', params.group);
    if (params.id_user) p.set('id_user', String(params.id_user));
    const qs = p.toString();
    return request<{ code: string; msg: string; total: number; rows: RentalReportRow[] }>(
      `/api/v1/rental/report${qs ? '?' + qs : ''}`, { method: 'GET' });
  },

  // --- Vehicle Brand & Model (master, admin-only CRUD; List terbuka) ---
  vehicleBrandList: () =>
    request<{ code: string; msg: string; total: number; rows: VehicleBrandRow[] }>(
      '/api/v1/vehicle-brand/list', { method: 'GET' }),
  vehicleBrandAdd: (body: VehicleBrandForm) =>
    request<{ code: string; msg: string }>('/api/v1/vehicle-brand/add', {
      method: 'POST', body: JSON.stringify(body),
    }),
  vehicleBrandEdit: (body: VehicleBrandForm & { id: number }) =>
    request<{ code: string; msg: string }>('/api/v1/vehicle-brand/edit', {
      method: 'POST', body: JSON.stringify(body),
    }),
  vehicleBrandDelete: (id: number) =>
    request<{ code: string; msg: string }>('/api/v1/vehicle-brand/delete', {
      method: 'POST', body: JSON.stringify({ id }),
    }),

  vehicleModelList: (params: { brand_id?: number; only_active?: boolean } = {}) => {
    const p = new URLSearchParams();
    if (params.brand_id)    p.set('brand_id', String(params.brand_id));
    if (params.only_active) p.set('only_active', '1');
    const qs = p.toString();
    return request<{ code: string; msg: string; total: number; rows: VehicleModelRow[] }>(
      `/api/v1/vehicle-model/list${qs ? '?' + qs : ''}`, { method: 'GET' });
  },
  vehicleModelAdd: (body: VehicleModelForm) =>
    request<{ code: string; msg: string }>('/api/v1/vehicle-model/add', {
      method: 'POST', body: JSON.stringify(body),
    }),
  vehicleModelEdit: (body: VehicleModelForm & { id: number }) =>
    request<{ code: string; msg: string }>('/api/v1/vehicle-model/edit', {
      method: 'POST', body: JSON.stringify(body),
    }),
  vehicleModelDelete: (id: number) =>
    request<{ code: string; msg: string }>('/api/v1/vehicle-model/delete', {
      method: 'POST', body: JSON.stringify({ id }),
    }),

  // --- GPS brand + model (untuk dropdown di EditVehicleDrawer) ---
  gpsBrandList: () =>
    request<{ code: string; msg: string; total: number; rows: GpsBrandRow[] }>(
      '/api/v1/gps-brand/list', { method: 'GET' }),
  gpsBrandAdd: (body: GpsBrandForm) =>
    request<{ code: string; msg: string }>('/api/v1/gps-brand/add', {
      method: 'POST', body: JSON.stringify(body),
    }),
  gpsBrandEdit: (body: GpsBrandForm & { id: number }) =>
    request<{ code: string; msg: string }>('/api/v1/gps-brand/edit', {
      method: 'POST', body: JSON.stringify(body),
    }),
  gpsBrandDelete: (id: number) =>
    request<{ code: string; msg: string }>('/api/v1/gps-brand/delete', {
      method: 'POST', body: JSON.stringify({ id }),
    }),

  gpsModelList: (params: { brand_id?: number; only_active?: boolean } = {}) => {
    const p = new URLSearchParams();
    if (params.brand_id)    p.set('brand_id', String(params.brand_id));
    if (params.only_active) p.set('only_active', '1');
    const qs = p.toString();
    return request<{ code: string; msg: string; total: number; rows: GpsModelRow[] }>(
      `/api/v1/gps-model/list${qs ? '?' + qs : ''}`, { method: 'GET' });
  },
  gpsModelAdd: (body: GpsModelForm) =>
    request<{ code: string; msg: string }>('/api/v1/gps-model/add', {
      method: 'POST', body: JSON.stringify(body),
    }),
  gpsModelEdit: (body: GpsModelForm & { id: number }) =>
    request<{ code: string; msg: string }>('/api/v1/gps-model/edit', {
      method: 'POST', body: JSON.stringify(body),
    }),
  gpsModelDelete: (id: number) =>
    request<{ code: string; msg: string }>('/api/v1/gps-model/delete', {
      method: 'POST', body: JSON.stringify({ id }),
    }),

  // --- Platform profile (single row, header invoice) ---
  // GET terbuka untuk semua user (render invoice header). Update admin only.
  platformProfileGet: () =>
    request<{ code: string; msg: string; data: PlatformProfile }>(
      '/api/v1/platform-profile/get', { method: 'GET' }),
  platformProfileUpdate: (body: PlatformProfile) =>
    request<{ code: string; msg: string }>('/api/v1/platform-profile/update', {
      method: 'POST', body: JSON.stringify(body),
    }),
  // Public â€” tidak butuh auth (dipakai login page). Subset field saja.
  platformProfilePublic: () =>
    fetch('/api/v1/public/platform-profile')
      .then((r) => r.json() as Promise<{ code: string; msg: string; data: PlatformPublicProfile }>),

  // --- Current user self-service profile ---
  myProfile: () =>
    request<{ code: string; msg: string; data: MyProfileData }>(
      '/api/v1/user/profile', { method: 'GET' }),
  myProfileUpdate: (body: { real_name: string; phone: string; email: string; address: string; city: string }) =>
    request<{ code: string; msg: string }>('/api/v1/user/profile/update', {
      method: 'POST', body: JSON.stringify(body),
    }),
  myProfileChangePassword: (body: { current_password: string; new_password: string }) =>
    request<{ code: string; msg: string }>('/api/v1/user/profile/password', {
      method: 'POST', body: JSON.stringify(body),
    }),

  // --- Share position (authenticated CRUD + public view) ---
  sharePositionCreate: (body: { vh_id: number; duration_minutes: number; label?: string }) =>
    request<{ code: string; msg: string; data: SharePositionRow }>(
      '/api/v1/share-position/create', {
        method: 'POST', body: JSON.stringify(body),
      }),
  sharePositionList: () =>
    request<{ code: string; msg: string; total: number; rows: SharePositionRow[] }>(
      '/api/v1/share-position/list', { method: 'GET' }),
  sharePositionRevoke: (id: number) =>
    request<{ code: string; msg: string }>('/api/v1/share-position/revoke', {
      method: 'POST', body: JSON.stringify({ id }),
    }),
  // Public â€” tidak butuh auth. Dipanggil dari halaman /public/share/:token.
  // Pakai fetch langsung (bukan request helper yg attach Authorization).
  sharePositionPublicGet: (token: string) =>
    fetch(`/api/v1/public/share/${encodeURIComponent(token)}`)
      .then((r) => r.json() as Promise<{ code: string; msg: string; data: SharePublicData }>),

  // --- Command history ---
  commandHistoryList: (params: { vh_id?: number; limit?: number } = {}) => {
    const p = new URLSearchParams();
    if (params.vh_id) p.set('vh_id', String(params.vh_id));
    if (params.limit) p.set('limit', String(params.limit));
    const qs = p.toString();
    return request<{ code: string; msg: string; total: number; rows: CommandHistoryRow[] }>(
      `/api/v1/command-history/list${qs ? '?' + qs : ''}`, { method: 'GET' });
  },
  commandHistoryDelete: (id: number) =>
    request<{ code: string; msg: string }>('/api/v1/command-history/delete', {
      method: 'POST', body: JSON.stringify({ id }),
    }),

  // --- Service record (riwayat servis kendaraan) ---
  serviceRecordList: (params: { vh_id?: number; limit?: number } = {}) => {
    const p = new URLSearchParams();
    if (params.vh_id) p.set('vh_id', String(params.vh_id));
    if (params.limit) p.set('limit', String(params.limit));
    const qs = p.toString();
    return request<{ code: string; msg: string; total: number; rows: ServiceRecordRow[] }>(
      `/api/v1/service-record/list${qs ? '?' + qs : ''}`, { method: 'GET' });
  },
  serviceRecordGet: (id: number) =>
    request<{ code: string; msg: string; data: ServiceRecordRow }>(
      `/api/v1/service-record/get/${id}`, { method: 'GET' }),
  serviceRecordAdd: (body: ServiceRecordForm) =>
    request<{ code: string; msg: string }>('/api/v1/service-record/add', {
      method: 'POST', body: JSON.stringify(body),
    }),
  serviceRecordEdit: (body: ServiceRecordForm & { id: number }) =>
    request<{ code: string; msg: string }>('/api/v1/service-record/edit', {
      method: 'POST', body: JSON.stringify(body),
    }),
  serviceRecordDelete: (id: number) =>
    request<{ code: string; msg: string }>('/api/v1/service-record/delete', {
      method: 'POST', body: JSON.stringify({ id }),
    }),

  // --- Notification preference (per current user â€” self-service) ---
  notificationPrefGet: () =>
    request<{ code: string; msg: string; data: NotificationPref }>(
      '/api/v1/notification-pref/get', { method: 'GET' }),
  notificationPrefUpdate: (body: NotificationPref) =>
    request<{ code: string; msg: string }>('/api/v1/notification-pref/update', {
      method: 'POST', body: JSON.stringify(body),
    }),

  // --- WhatsApp (proxy ke whatsapp-service via NATS request-reply) ---
  whatsappStatus: () =>
    request<{ code: string; msg: string; data: WhatsAppStatus }>(
      '/api/v1/whatsapp/status', { method: 'GET' }),
  whatsappConnect: () =>
    request<{ code: string; msg: string; data: WhatsAppConnectResult }>(
      '/api/v1/whatsapp/connect', { method: 'POST' }),
  whatsappQR: () =>
    request<{ code: string; msg: string; data: WhatsAppQRResult }>(
      '/api/v1/whatsapp/qr', { method: 'GET' }),
  whatsappLogout: () =>
    request<{ code: string; msg: string; data: { success: boolean; error?: string } }>(
      '/api/v1/whatsapp/logout', { method: 'POST' }),

  // --- Tenant CRUD (admin only di backend via requireAdmin) ---
  tenantList: (includeDeleted = false) =>
    request<{ code: string; msg: string; total: number; rows: TenantRow[] }>(
      `/api/v1/tenant/list${includeDeleted ? '?include_deleted=1' : ''}`, { method: 'GET' }),
  tenantGet: (id: number) =>
    request<{ code: string; msg: string; data: TenantRow }>(
      `/api/v1/tenant/get/${id}`, { method: 'GET' }),
  tenantAdd: (body: TenantForm) =>
    request<{ code: string; msg: string }>('/api/v1/tenant/add', {
      method: 'POST', body: JSON.stringify(body),
    }),
  tenantEdit: (body: TenantForm) =>
    request<{ code: string; msg: string }>('/api/v1/tenant/edit', {
      method: 'POST', body: JSON.stringify(body),
    }),
  tenantDelete: (id: number) =>
    request<{ code: string; msg: string }>('/api/v1/tenant/delete', {
      method: 'POST', body: JSON.stringify({ id }),
    }),
  tenantRestore: (id: number) =>
    request<{ code: string; msg: string }>('/api/v1/tenant/restore', {
      method: 'POST', body: JSON.stringify({ id }),
    }),

  // --- Vehicle group ---
  // Scope by effectiveUserId: kalau admin/reseller impersonate user lain,
  // kirim id_user di query/body supaya backend balikin group user target.
  vehicleGroupList: (idUser?: number) =>
    request<VehicleGroupListResponse>(
      `/api/v1/vehicle-group/list${idUser ? `?id_user=${idUser}` : ''}`,
      { method: 'POST' },
    ),
  vehicleGroupAdd: (body: { vh_group: string; id_user?: number }) =>
    request<{ code: string; msg: string }>('/api/v1/vehicle-group/add', {
      method: 'POST', body: JSON.stringify(body),
    }),
  vehicleGroupUpdate: (body: { id: number; vh_group: string }) =>
    request<{ code: string; msg: string }>('/api/v1/vehicle-group/update', {
      method: 'POST', body: JSON.stringify(body),
    }),
  vehicleGroupDelete: (id: number) =>
    request<{ code: string; msg: string }>('/api/v1/vehicle-group/delete', {
      method: 'POST', body: JSON.stringify({ id }),
    }),

  // --- Playback ---
  playbackRoute: (body: { vehicle_id: number; from: string; to: string; limit?: number; min_park_minutes?: number }) =>
    request<PlaybackRouteResponse>('/api/v1/playback/route', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  temperatureZipDownload: async (params: { id_user?: number; vh_ids: number[] | 'all'; from: string; to: string; workers?: number }): Promise<Response> => {
    const token = (await import('@/store/auth-store')).useAuthStore.getState().token;
    const p = new URLSearchParams();
    if (params.id_user) p.set('id_user', String(params.id_user));
    p.set('vh_ids', params.vh_ids === 'all' ? 'all' : params.vh_ids.join(','));
    p.set('fdate', params.from);
    p.set('tdate', params.to);
    if (params.workers) p.set('workers', String(params.workers));
    return fetch((import.meta.env.VITE_API_BASE ?? '') + `/api/v1/reports/temperature/zip?${p.toString()}`, {
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
  },
  temperaturePdfDownload: async (params: { id_user?: number; vh_id: number; from: string; to: string }): Promise<Response> => {
    const token = (await import('@/store/auth-store')).useAuthStore.getState().token;
    const p = new URLSearchParams();
    if (params.id_user) p.set('id_user', String(params.id_user));
    p.set('id', String(params.vh_id));
    p.set('fdate', params.from);
    p.set('tdate', params.to);
    return fetch((import.meta.env.VITE_API_BASE ?? '') + `/api/v1/reports/temperature/pdf?${p.toString()}`, {
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
  },
  temperaturePdfZipDownload: async (params: { id_user?: number; vh_ids: number[] | 'all'; from: string; to: string; workers?: number }): Promise<Response> => {
    const token = (await import('@/store/auth-store')).useAuthStore.getState().token;
    const p = new URLSearchParams();
    if (params.id_user) p.set('id_user', String(params.id_user));
    p.set('vh_ids', params.vh_ids === 'all' ? 'all' : params.vh_ids.join(','));
    p.set('fdate', params.from);
    p.set('tdate', params.to);
    if (params.workers) p.set('workers', String(params.workers));
    return fetch((import.meta.env.VITE_API_BASE ?? '') + `/api/v1/reports/temperature/pdf/zip?${p.toString()}`, {
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
  },
  /**
   * allDeviceExcelDownload — "Export All": workbook XLSX berisi SELURUH
   * kendaraan di sistem (endpoint GET /api/v1/reports/alldevice, admin-only /
   * level_id=1; reseller ditolak 403). `id_user` opsional untuk mempersempit
   * ke satu pemilik — dipakai saat filter user aktif di halaman Kendaraan.
   * Return raw Response supaya caller bisa stream blob ke download browser.
   */
  allDeviceExcelDownload: async (idUser?: number): Promise<Response> => {
    const token = (await import('@/store/auth-store')).useAuthStore.getState().token;
    const p = new URLSearchParams();
    if (idUser && idUser > 0) p.set('id_user', String(idUser));
    const qs = p.toString();
    return fetch((import.meta.env.VITE_API_BASE ?? '') + `/api/v1/reports/alldevice${qs ? '?' + qs : ''}`, {
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
  },

  // --- Trip report ---
  tripReport: (params: { id: number; from: string; to: string }) => {
    const p = new URLSearchParams({
      id: String(params.id),
      fdate: params.from,
      tdate: params.to,
    });
    return request<TripReportResponse>(`/api/v1/reports/trip?${p.toString()}`, { method: 'GET' });
  },

  // --- Users â€” scope di backend by JWT level_id (lihat user_v1.go List):
  //     level_id 1 â†’ semua user
  //     level_id 4 â†’ user dia sendiri + bawahan (reseller_id=caller)
  //     lainnya     â†’ cuma user dia sendiri
  userList: () =>
    request<UserListResponse>('/api/v1/user/list', { method: 'POST' }),

  resellerUsers: () =>
    request<UserListResponse>('/api/v1/user/resellers', { method: 'POST' }),
};

// =============================================================================
// User list types (untuk UserPicker di drawer)
// =============================================================================

export interface ListedUser {
  id: number;
  real_name: string;
  login: string;
  phone?: string;
  city?: string;
  address?: string;
  email?: string;
  level?: string;
  level_id: number;
  reseller_id?: number;
  status?: string;
  state?: number;
}

export interface UserListResponse {
  code: string;
  msg: string;
  total: number;
  rows: ListedUser[];
}

export { ApiError };

